import { ReactNode, Suspense } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { useSearchParams, Link } from 'react-router-dom';
import { ErrorBoundary } from '@sentry/react';
import { twJoin } from 'tailwind-merge';
import { ButtonIcon, Loader } from '@statsbomb/kitbag-components';
import { playerIdAtom } from '@/atoms/player/player';
import { PLAYER_BASE_ROUTE } from '@/hooks/navigation/usePlayerNavigationItem';
import { METRIC_KEY, PLAYER_ID } from '@/consts/searchKeys';
import { CloseCardHeaderButton } from '@/components/button/CloseCardHeaderButton';
import { userNavOverridesAtom } from '@/atoms/queries/user';
import { unwrappedPlayerIdMappingAtom } from '@/atoms/queries/players';
import { PlayerPreviewTitle } from './PlayerPreviewTitle';
import { PlayerPreviewInfo } from './PlayerPreviewInfo';

const PlayerDashboardLink = ({ children }: { children: ReactNode }) => {
  const { i18n } = useTranslation('preview');
  const [searchParams] = useSearchParams();
  const playerId = searchParams.get(PLAYER_ID);
  const isPlayerClassicXp = useAtomValue(userNavOverridesAtom).player;
  const playersMapping = useAtomValue(unwrappedPlayerIdMappingAtom);
  const iqId = playersMapping[Number(playerId)];

  return isPlayerClassicXp ? (
    <a
      href={`${PLAYER_BASE_ROUTE}/${iqId}/dashboard`}
      className={twJoin(!iqId && 'cursor-default pointer-events-none', 'self-start')}
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  ) : (
    <Link
      to={`/${i18n.language}/player/${playerId}/dashboard`}
      target="_blank"
      rel="noopener noreferrer"
      className="self-start"
    >
      {children}
    </Link>
  );
};

const ViewDashboardButton = ({ isLoading, isDisabled }: { isLoading?: boolean; isDisabled?: boolean }) => {
  const { t } = useTranslation('preview');

  return (
    <ButtonIcon
      icon="Start"
      displayText="right"
      size="small"
      variant="secondary"
      disabled={isLoading || isDisabled}
      isPending={isLoading}
      as="span"
    >
      {t('viewDashboard')}
    </ButtonIcon>
  );
};

export const PlayerPreviewHeader = () => {
  const setPlayerId = useSetAtom(playerIdAtom);

  return (
    <header className="flex flex-col p-4 border-b border-canvas-secondary-main">
      <div className="flex justify-between items-center">
        <Suspense fallback={<Loader size="1rem" />}>
          <PlayerPreviewTitle />
        </Suspense>
        <CloseCardHeaderButton
          navigationPath=".."
          paramsToDelete={[METRIC_KEY, `playerId`]}
          onClick={() => setPlayerId(null)}
        />
      </div>
      <div className="pb-4">
        <Suspense fallback={<Loader size="1rem" />}>
          <PlayerPreviewInfo />
        </Suspense>
      </div>
      <ErrorBoundary fallback={<ViewDashboardButton isDisabled />}>
        <Suspense fallback={<ViewDashboardButton isLoading />}>
          <PlayerDashboardLink>
            <ViewDashboardButton />
          </PlayerDashboardLink>
        </Suspense>
      </ErrorBoundary>
    </header>
  );
};
